// Libraries
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import store from '@/store/store'

// Reference Components
import AllCompanyList from '@/views/CompanyList-All.vue'
import AuthorityList from '@/views/CompanyList-Authorities.vue'
import ChangePassword from '@/views/ChangePassword.vue'
import CompanyDetails from '@/views/CompanyDetails.vue'
import CompanyPricingSummary from '@/views/CompanyPricingSummary.vue'
import CustomerList from '@/views/CompanyList-Customers.vue'
import CustomerLogos from '@/views/CustomerLogos.vue'
import Dashboard from '@/views/Dashboard.vue'
import MailingList from '@/views/MailingList.vue'
import NotFound from '@/views/NotFound.vue'
import OutboundBwofEmails from '@/views/OutboundBwofEmails.vue'
import OutboundEvacEmails from '@/views/OutboundEvacEmails.vue'
import OwnerList from '@/views/CompanyList-Owners.vue'
import PropertyDetails from '@/views/PropertyDetails.vue'
import PropertyList from '@/views/PropertyList.vue'
import Reports from '@/views/Reports.vue'
import ServiceDefinitions from '@/views/ServiceDefinitions.vue'
import SupplierList from '@/views/CompanyList-Suppliers.vue'
import UserDetails from '@/views/UserDetails.vue'
import UserList from '@/views/UserList.vue'

/// Authentication function that will check if the User is an internal Compass user (to be called in beforeEnter)
const allowOnlyInternal = () => {
	return store.getters.isInternalUser;
}

/// Authentication function allowing the route to always pass (to be called in beforeEnter)
const allowAnyUser = () => {
	return true;
}

// Routes
const routes: Array<RouteRecordRaw> = [
	{
		path: '/',
		name: 'Dashboard',
		meta: { authorize: allowAnyUser },
		component: Dashboard
	},

	{
		path: '/change-password',
		name: 'ChangePassword',
		meta: { authorize: allowAnyUser },
		component: ChangePassword
	},

	// Company List variations (authorities, customers, suppliers, owners, all)
	{
		path: '/authorities',
		name: 'CompanyList-Authorities',
		meta: { authorize: allowOnlyInternal },
		component: AuthorityList,
	},
	{
		path: '/companies',
		name: 'CompanyList-All',
		meta: { authorize: allowOnlyInternal },
		component: AllCompanyList
	},
	{
		path: '/customers',
		name: 'CompanyList-Customers',
		meta: { authorize: allowOnlyInternal },
		component: CustomerList,
	},
	{
		path: '/suppliers',
		name: 'CompanyList-SupplierList',
		meta: { authorize: allowOnlyInternal },
		component: SupplierList,
	},
	{
		path: '/owners',
		name: 'CompanyList-Owners',
		meta: { authorize: allowOnlyInternal },
		component: OwnerList,
	},

	// Company editor
	{
		path: '/companies/:companyId',
		name: 'CompanyDetails',
		meta: { authorize: allowOnlyInternal },
		component: CompanyDetails,
		props: route => ({ companyId: Number(route.params.companyId) })
	},

	// Company editor - new record creation
	{
		path: '/companies/new',
		name: 'NewCompany',
		meta: { authorize: allowOnlyInternal },
		component: CompanyDetails,
		props: (route) => ({
			companyId: -1,
			companyType: Number(route.query.companyType) 
		})
	},

	// Company Pricing Summary
	{
		path: '/company-pricing-summary/:companyId',
		name: 'CompanyPricingSummary',
		meta: { authorize: allowOnlyInternal },
		component: CompanyPricingSummary,
		props: true
	},

	// Mailing list
	{
		path: '/mailing-list',
		name: 'MailingList',
		meta: { authorize: allowOnlyInternal },
		component: MailingList
	},

	// Property list
	{
		path: '/properties',
		name: 'PropertyList',
		meta: { authorize: allowOnlyInternal },
		component: PropertyList
	},

	// Property editor - new record creation
	{
		path: '/property/new',
		name: 'NewProperty',
		meta: { authorize: allowOnlyInternal },
		component: PropertyDetails,
		props: { propertyId: -1 }
	},

	// Property editor - existing record
	{
		path: '/property/:propertyId',
		name: 'PropertyDetails',
		meta: { authorize: allowAnyUser },
		component: PropertyDetails,
		props: true
	},

	// Outbound BWOF Emails
	{
		path: '/outbound-bwof-emails',
		name: 'OutboundBwofEmails',
		meta: { authorize: allowOnlyInternal },
		component: OutboundBwofEmails,
	},

	// Outbound Evac Emails
	{
		path: '/outbound-evac-emails',
		name: 'OutboundEvacEmails',
		meta: { authorize: allowOnlyInternal },
		component: OutboundEvacEmails,
	},

	// Reports
	{
		path: '/reports',
		name: 'Reports',
		meta: { authorize: allowOnlyInternal },
		component: Reports,
	},

	// Service definitions
	{
		path: '/services',
		name: 'ServiceDefinitions',
		meta: { authorize: allowOnlyInternal },
		component: ServiceDefinitions,
	},

	// Customer logos
	{
		path: '/customer-logos',
		name: 'CustomerLogos',
		meta: { authorize: allowOnlyInternal },
		component: CustomerLogos,
	},


	// User list
	{
		path: '/users',
		name: 'UserList',
		meta: { authorize: allowOnlyInternal },
		component: UserList
	},

	// User edit
	{
		path: '/user/:loginId',
		name: 'UserDetails',
		meta: { authorize: allowOnlyInternal },
		component: UserDetails,
		props: true
	},

	// User create
	{
		path: '/user/new',
		name: 'NewUser',
		meta: { authorize: allowOnlyInternal },
		component: UserDetails,
		props: { loginId: -1 }
	},

	// Catchall - go to not found
	{
		path: '/:catchAll(.*)',
		name: 'NotFound',
		meta: { authorize: allowAnyUser },
		component: NotFound
	}
]

// Create
const router = createRouter({
	history: createWebHistory(process.env.BASE_URL),
	routes
})

// TODO: To fully decouple the router from the store, this should be moved to a separate route-authorization module, which accesses the router and store singletons.
router.beforeEach((to, _) => {
	// When accessing a route without logging in, the login form will be shown by the App.vue implementation, and we want to retain the route should the login succeed
	if (!store.getters.isAuthenticated) {
		return true;
	}

	// Redirect to home page if accessing a page we are not authorized to access
	if (typeof (to.meta.authorize) !== 'function') {
		return '/meta.authorize-not-set-for-route-' + to.path;
	}

	const allowed = to.meta.authorize();
	// If role is not authorised to the requested route, redirect to home page
	if (allowed) {
		return true;
	} else {
		// If role is not authorised to the requested route, redirect to home page
		return '/';
	}
})

export default router
